import React, { useCallback, useState } from "react";
import { Button, Dropdown, Tooltip, message } from "antd";
import { ExportOutlined, DownOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, FileZipOutlined, TeamOutlined } from "@ant-design/icons";
import { usePartnerTrades } from "components/Partners/partnerTrades";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import { exportIssues, getExportContractors, getExportOptions } from "api/issue/issueApi";
import { askExportOptions } from "./exportOptionsDialog";

const NAMES = { bcf: "BCF", pdf: "PDF", excel: "Excel", csv: "CSV" };

// "Export BCF / PDF / Excel / CSV" - one dropdown button used by the Issues view (its
// current filters), the coordination dashboard (whole project) and the
// single issue page (that issue). `filters` is whatever exportIssues
// accepts; `onDone` gets { format, count, filename }.
//
// ONE CONTRACTOR'S WORK IS A SUBMENU, NOT A SEPARATE BUTTON (owner,
// 2026-10-06: "add a filter on the export to onl;y show entries, from
// specific partner/contractor"). The document then holds the issues that
// contractor's people raised, hold or resolved, or that the contractor logged
// hours on (widened 2026-10-14: a tag of open issues BES raised exported
// empty), their hours, and their rates only -
// and says so on its first page with the count it left out, because an export
// that looks complete and is not is the worst artefact we can produce.
//
// THE PARTNERS ARE FETCHED WHEN THE MENU OPENS, not on mount: this component
// renders on three pages, most opens are the ordinary whole-project export,
// and a roster read on every page load would be a request nobody asked for.
// The list comes from the server (active participants of this project only) -
// never every company, which would tell whoever opens the menu who else we
// work with.
export default function IssueExportMenu({ filters, label = "Export", tooltip, size, style, onDone }) {
  const [busy, setBusy] = useState(null);
  const [partners, setPartners] = useState(null);
  const projectId = filters && filters.projectId;
  const trades = usePartnerTrades(projectId);

  const run = async (format, contractor = null) => {
    // WHAT MAY GO IN IT, ASKED FIRST (owner, 2026-10-22). The server says
    // which boxes this person may be offered; with none, there is no dialog
    // and nothing changes for them. A failure to read the permissions offers
    // nothing rather than everything, and the export still runs as it does
    // today - a permissions lookup that is down must not stop people working.
    let include;
    try {
      const allowed = await getExportOptions(projectId);
      const answer = await askExportOptions({ allowed, formatName: NAMES[format] || "export" });
      if (answer === null) return;          // cancelled
      if (allowed && (allowed.hours || allowed.prices)) include = answer;
    } catch (e) {
      include = undefined;
    }
    setBusy(format);
    try {
      // THE TOOLBAR'S CONTRACTOR FILTER CARRIES unless this click names one.
      //
      // Writing `contractor: contractor || undefined` OVERWROTE it: the plain
      // "Export PDF" item passes null, so a list narrowed to one contractor
      // exported as the WHOLE PROJECT - every company and every rate - which
      // is exactly what the owner saw. An explicit choice from the submenu
      // still wins over the toolbar; it is a narrowing, not a replacement.
      const scoped = contractor || (filters && filters.contractor) || undefined;
      const r = await exportIssues(format, {
        ...(filters || {}),
        contractor: scoped,
        ...(include ? { includeHours: include.hours, includeEstimates: include.estimates, includePrices: include.prices } : {}),
      });
      const what = NAMES[format] || "BCF";
      const who = scoped && (partners || []).find((p) => p.companyId === scoped);
      const scope = who ? ` for ${who.companyName}` : "";
      message.success(r.count
        ? `Exported ${r.count} issue${r.count === 1 ? "" : "s"}${scope} as ${what} (${r.filename})`
        : `No issues match${scope} (${r.filename})`);
      onDone && onDone({ format, contractor: scoped || null, ...r });
    } catch (e) {
      message.error(e?.message || `${NAMES[format] || "BCF"} export failed`);
    } finally {
      setBusy(null);
    }
  };

  const loadPartners = useCallback(async () => {
    if (partners || !projectId) return;
    try {
      setPartners(await getExportContractors(projectId));
    } catch (e) {
      // AN EMPTY LIST, NOT A SILENT ONE: the submenu says the lookup failed
      // rather than reading as "this project has no partners", which is a
      // different and wrong statement.
      setPartners({ error: e?.message || "Could not load the partners on this project" });
    }
  }, [partners, projectId]);

  const formatItems = [
    { key: "bcf", icon: <FileZipOutlined />, label: "Export BCF (.bcfzip) - Revit / Navisworks / Solibri" },
    { key: "pdf", icon: <FilePdfOutlined />, label: "Export PDF - table + details + comments" },
    { key: "excel", icon: <FileExcelOutlined />, label: "Export Excel (.xlsx) - issue register" },
    { key: "csv", icon: <FileTextOutlined />, label: "Export CSV - issue register, one row per issue" },
  ];

  const contractorChildren = () => {
    if (!partners) return [{ key: "c-loading", label: "Loading…", disabled: true }];
    if (partners.error) return [{ key: "c-err", label: partners.error, disabled: true }];
    if (!partners.length) return [{ key: "c-none", label: "No partner companies on this project", disabled: true }];
    // SHORT LABELS UNDER THE CONTRACTOR'S NAME. Repeating the full
    // "Export PDF - table + details + comments" inside a submenu already
    // headed "BES" reads as a second copy of the same command rather than a
    // narrower one, and two identically labelled items one level apart are
    // ambiguous to click.
    const SHORT = { bcf: "BCF (.bcfzip)", pdf: "PDF", excel: "Excel (.xlsx)", csv: "CSV" };
    return partners.map((p) => ({
      key: `c-${p.companyId}`,
      // The partner with its trades beneath (owner, 2026-10-16).
      label: <PartnerWithTrades name={p.companyName || "(unnamed)"} trades={trades.tradesOf(p.companyId)} />,
      children: formatItems.map((f) => ({ ...f, key: `${p.companyId}|${f.key}`, label: SHORT[f.key] })),
    }));
  };

  const items = [...formatItems];
  if (projectId) {
    items.push({ type: "divider", key: "d1" });
    items.push({
      key: "by-contractor",
      icon: <TeamOutlined />,
      label: "One contractor only - their issues",
      children: contractorChildren(),
    });
  }

  const onClick = ({ key }) => {
    if (key.includes("|")) {
      const [companyId, format] = key.split("|");
      run(format, companyId);
      return;
    }
    if (NAMES[key]) run(key);
  };

  const btn = (
    <Dropdown
      menu={{ items, onClick }}
      trigger={["click"]}
      disabled={!!busy}
      onOpenChange={(open) => { if (open) loadPartners(); }}
    >
      <Button icon={<ExportOutlined />} loading={!!busy} size={size} style={style}>
        {busy ? `Building ${NAMES[busy] || "BCF"}…` : label} <DownOutlined style={{ fontSize: 10 }} />
      </Button>
    </Dropdown>
  );
  return tooltip ? <Tooltip title={tooltip}>{btn}</Tooltip> : btn;
}
